import cloneDeep from "lodash.clonedeep";
import isEqual from "lodash.isequal";
import { findNodeId } from "../../../utils/tree";
// structure.js imports withEndpointRefs from here: both are used only when
// the functions run, so the import cycle is harmless
import { deriveEndpoint } from "../structure";

// The conditions and the scheduling of the variants edited on a variant page
// are not in its form: they are collected (keyed by the block of the
// variant) and applied to the endpoint on Save. Keyed by block, not by
// index: a variant moved meanwhile (a drag of the tabs, a draft) keeps its
// own changes. Returns the endpoint (changed in place).
export function applyVariantChanges(
  endpoint,
  conditions = new Map(),
  schedulings = new Map()
) {
  const byBlock = (blockId) =>
    endpoint.variants?.find((variant) => variant.blockId === blockId);

  for (const [blockId, { condition, conditionRaw }] of conditions.entries()) {
    const variant = byBlock(blockId);
    if (!variant) continue;
    variant.condition = condition;
    variant.conditionRaw = conditionRaw;
  }

  // Written only when they say something else (the rule of withSchedule,
  // scheduleDiagram/schedule.js: a missing field is no scheduling, scheduled
  // may be 1/0): the box hands them over at every edit, also "not
  // scheduled" for a variant that has no such fields, and a change undone
  // must give the stored variant back.
  for (const [blockId, { scheduled, scheduling }] of schedulings.entries()) {
    const variant = byBlock(blockId);
    if (!variant) continue;
    if (
      Boolean(variant.scheduled) === Boolean(scheduled) &&
      isEqual(variant.scheduling ?? [], scheduling ?? [])
    )
      continue;
    variant.scheduled = scheduled;
    variant.scheduling = scheduling;
  }

  return endpoint;
}

// The endpoint the Save of a variant page sends with the page: only when
// conditions or scheduling of its variants were changed on the page (the
// endpoint row may be drafted by another page: a plain block Save does not
// send it, nor gets refused for it). Built on the given row (the draft row
// when drafted by the property). doRefs: the refs of the endpoint
// (doEndpointRefs). baseVersion: the version of the endpoint the changes
// were made on (the page's pin, see endpointPinConflict); without it, the
// row's own: refused if someone saved it meanwhile.
export function endpointExtra(
  endpoint,
  conditions,
  schedulings,
  doRefs,
  { baseVersion } = {}
) {
  if (!endpoint) return null;
  if (!(conditions?.size > 0) && !(schedulings?.size > 0)) return null;

  const data = applyVariantChanges(
    cloneDeep(endpoint),
    conditions,
    schedulings
  );
  data.haveScheduledVariants = (data.variants ?? []).some(
    (variant) => variant.scheduled
  )
    ? 1
    : 0;

  return {
    accessor: "endpoints",
    Id: data.id,
    prevId: data.id,
    isNew: false,
    baseVersion: Number(baseVersion ?? endpoint.version ?? 0),
    data: doRefs(data),
    notify: false
  };
}

// The rows a variant page changes besides its block (the Item's form), as
// draft.change ops: the stashExtra of the page, put into the draft with the
// form row when the page is stashed (a variant tab switch, see
// hooks/draft/stashForm.js). The draft side of saveExtra:
// - endpoints:E, when conditions or scheduling of its variants were changed
//   on the page (the Maps, keyed by block), applied to the endpoint row the
//   updater gets (the draft row when drafted). Its derived fields as
//   deriveEndpoint keeps them (structure.js): refs.require built again only
//   when its inputs changed, haveScheduledVariants changed only when it no
//   longer says whether a variant is scheduled (as 1/0), prevReference the
//   stored one; each back to the stored value when what it is derived from
//   is, so that applying the stored condition back leaves no draft.
//   Otherwise the same row as endpointExtra.
// - properties:P, the label of the page's node in the tree, when the title
//   of the form is not the one it was opened with.
// stored: the stored endpoint row (storedRow), none for an endpoint created
// in the draft. doRefs: doEndpointRefs. base: the endpoint the Maps were
// made on (the page's pin): stored at another version (someone saved or
// deleted it since), the change is refused (the updater throws
// endpointChangedError: the navigation stays, with a warning). The Maps are
// not changed: the page clears them once the change committed (all or
// none).
export function variantStashOps({
  endpointId,
  propertyId,
  blockId,
  title,
  initialTitle,
  conditions,
  schedulings,
  stored,
  base,
  doRefs
}) {
  const ops = [];

  if (endpointId && (conditions?.size > 0 || schedulings?.size > 0)) {
    ops.push({
      kind: "edit",
      accessor: "endpoints",
      id: endpointId,
      updater: (row) => {
        if (endpointPinConflict(base, stored))
          throw endpointChangedError(base, stored);
        const before = cloneDeep(row);
        applyVariantChanges(row, conditions, schedulings);
        return deriveEndpoint(row, before, stored ?? null, doRefs);
      }
    });
  }

  if (propertyId && title && title !== initialTitle) {
    ops.push({
      kind: "edit",
      accessor: "properties",
      id: propertyId,
      updater: (row) => {
        const [node] = Array.isArray(row.tree)
          ? findNodeId(blockId, row.tree)
          : [];
        if (node && node.label !== title) node.label = title;
        return row;
      }
    });
  }

  return ops;
}

// What the refs of an endpoint are built from (doEndpointRefs): its blocks,
// params, decorators and conversion packs.
const refInputs = (row) => ({
  blocks: [row?.blockId, ...(row?.variants ?? []).map((v) => v?.blockId)],
  paramsId: row?.paramsId,
  input: row?.input?.decorators ?? [],
  output: row?.output?.decorators ?? [],
  packs: (row?.output?.filters?.packs ?? []).map((p) => p?.conversionPackId)
});

// The refs are built again (doRefs) only when what they are built from
// changed between the row before an edit and after it: a play/pause or a
// siteMap flag leaves the row's refs as stored, so that undoing it gives the
// stored row back (and the draft empties).
export function withEndpointRefs(before, after, doRefs) {
  return isEqual(refInputs(before), refInputs(after)) ? after : doRefs(after);
}

const versionOf = (row) => Number(row?.version ?? 0);

// The conditions and scheduling changed on a variant page are made on the
// endpoint as stored when the first of them was (base: the page's pin, a
// copy of storedRow). Stored now at another version, or deleted: someone
// saved it since, and the changes would overwrite their version (the
// endpoint is no form of the page, nothing holds it). The conflict, in the
// shape of a 409's ({ type, accessor, id, action, baseVersion,
// storedVersion, lastSaved }), or null (no pin, or the same version).
// stored: storedRow("endpoints", id) now.
export function endpointPinConflict(base, stored) {
  if (!base) return null;
  const deleted = !stored;
  if (!deleted && versionOf(stored) === versionOf(base)) return null;
  return {
    type: "endpoints",
    accessor: "endpoints",
    id: base.id,
    action: deleted ? "delete" : "update",
    baseVersion: versionOf(base),
    storedVersion: deleted ? null : versionOf(stored),
    lastSaved: deleted ? undefined : stored.lastSaved
  };
}

// What endpointPinConflict reads of the stored row of accessor:id ({ id,
// version, lastSaved }), with no copy of it: storedRow copies the journal's
// original of a drafted row (a whole endpoint, its conditions included),
// too much for a render. undefined when it is not stored (deleted, or
// created in the draft), as storedRow. store: the draft store (ownerOf,
// entries); peek(accessor, id): MemoryDB's row.
export function storedStamp(store, accessor, id, peek) {
  // Drafted: the journal's original (null when the draft created it)
  const drafted = store.storedStampOf(accessor, id);
  if (drafted !== undefined) return drafted ?? undefined;
  const row = peek(accessor, id);
  return row
    ? { id: row.id, version: row.version, lastSaved: row.lastSaved }
    : undefined;
}

// The error of a stash refused for it (code "CHANGED"; the navigation shows
// its message: "your changes can't be kept (...)")
export function endpointChangedError(base, stored) {
  const name = `the endpoint ${base?.reference ?? base?.id ?? ""}`.trim();
  const who = stored?.lastSaved?.username ?? "someone";
  const error = new Error(
    stored
      ? `${name} was saved by ${who} after you changed the conditions of its variants: reload it, or apply your changes on their version, first`
      : `${name} was deleted after you changed the conditions of its variants`
  );
  error.code = "CHANGED";
  error.accessor = "endpoints";
  error.id = base?.id;
  return error;
}
